import { useCallback, useEffect, useRef } from 'react';

export interface LocalDraft {
  content: string;
  title?: string;
  savedAt: string;
  entityType: string;
  entityId: string;
}

const DRAFT_PREFIX = 'atlasdraft-draft-';
const DRAFT_EXPIRY_DAYS = 7;

/**
 * Hook for immediate localStorage backup on every keystroke.
 * Provides bulletproof protection against browser crashes, tab closures, and offline scenarios.
 */
export function useLocalDraft(entityType: string, entityId: string | undefined) {
  const isOnlineRef = useRef(navigator.onLine);

  // Generate storage key
  const getStorageKey = useCallback(() => {
    if (!entityId) return null;
    return `${DRAFT_PREFIX}${entityType}-${entityId}`;
  }, [entityType, entityId]);

  // Save draft immediately to localStorage
  const saveDraft = useCallback((content: string, title?: string) => {
    const key = getStorageKey();
    if (!key) return false;

    try {
      const draft: LocalDraft = {
        content,
        title,
        savedAt: new Date().toISOString(),
        entityType,
        entityId: entityId!,
      };
      localStorage.setItem(key, JSON.stringify(draft));
      return true;
    } catch (error) {
      console.error('Failed to save local draft:', error);
      return false;
    }
  }, [getStorageKey, entityType, entityId]);

  // Get draft from localStorage
  const getDraft = useCallback((): LocalDraft | null => {
    const key = getStorageKey();
    if (!key) return null;

    try {
      const stored = localStorage.getItem(key);
      if (!stored) return null;

      const draft: LocalDraft = JSON.parse(stored);
      
      // Check if draft is expired
      const savedAt = new Date(draft.savedAt);
      const now = new Date();
      const daysDiff = (now.getTime() - savedAt.getTime()) / (1000 * 60 * 60 * 24);
      
      if (daysDiff > DRAFT_EXPIRY_DAYS) {
        localStorage.removeItem(key);
        return null;
      }

      return draft;
    } catch (error) {
      console.error('Failed to get local draft:', error);
      return null;
    }
  }, [getStorageKey]);

  // Clear draft from localStorage
  const clearDraft = useCallback(() => {
    const key = getStorageKey();
    if (!key) return;
    
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Failed to clear local draft:', error);
    }
  }, [getStorageKey]);

  // Check if a draft exists and is newer than the provided timestamp
  const hasFresherDraft = useCallback((lastSavedAt: Date | null): boolean => {
    const draft = getDraft();
    if (!draft) return false;
    if (!lastSavedAt) return true;
    
    const draftTime = new Date(draft.savedAt);
    return draftTime > lastSavedAt;
  }, [getDraft]);

  // Track online status
  useEffect(() => {
    const handleOnline = () => {
      isOnlineRef.current = true;
    };
    
    const handleOffline = () => {
      isOnlineRef.current = false;
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return {
    saveDraft,
    getDraft,
    clearDraft,
    hasFresherDraft,
    isOnline: () => isOnlineRef.current,
  };
}

/**
 * Clean up expired drafts from localStorage.
 * Call this periodically or on app startup.
 */
export function cleanupExpiredDrafts() {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(DRAFT_PREFIX));
    const now = new Date();

    keys.forEach(key => {
      try {
        const stored = localStorage.getItem(key);
        if (!stored) return;

        const draft: LocalDraft = JSON.parse(stored);
        const savedAt = new Date(draft.savedAt);
        const daysDiff = (now.getTime() - savedAt.getTime()) / (1000 * 60 * 60 * 24);

        if (daysDiff > DRAFT_EXPIRY_DAYS) {
          localStorage.removeItem(key);
        }
      } catch {
        // Remove corrupted entries
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Failed to cleanup drafts:', error);
  }
}

/**
 * Get all pending drafts for sync queue.
 */
export function getAllPendingDrafts(): LocalDraft[] {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(DRAFT_PREFIX));
    const drafts: LocalDraft[] = [];

    keys.forEach(key => {
      try {
        const stored = localStorage.getItem(key);
        if (stored) {
          drafts.push(JSON.parse(stored));
        }
      } catch {
        // Skip corrupted entries
      }
    });

    return drafts;
  } catch (error) {
    console.error('Failed to get pending drafts:', error);
    return [];
  }
}
